import storageConfig from '../../storage/allowed-origins';

// Shared with storage/storage-service.js — edit storage/allowed-origins.js, not this file.
const { STORAGE_ORIGINS, NON_SYNCABLE_KEYS } = storageConfig;

// Temporary kill-switch for cross-domain storage due to CORS limitations.
// When true, all operations become no-ops while preserving logs.
const CROSS_DOMAIN_STORAGE_DISABLED = true;

/** Keys (e.g. the auth token) that are never mirrored to other subdomains. */
export const isCrossDomainSyncableKey = (key: string): boolean =>
  typeof key === 'string' && !NON_SYNCABLE_KEYS.includes(key);

const getStorageDomain = (): string => {
  const { hostname } = window.location;
  if (hostname.includes('localhost')) {
    return 'dev-storage.respona.com';
  }
  if (hostname.includes('dev') && hostname.includes('respona.com')) {
    return 'dev-storage.respona.com';
  }
  return 'storage.respona.com';
};

const STORAGE_DOMAIN = getStorageDomain();
const STORAGE_ORIGIN = `https://${STORAGE_DOMAIN}`;

type StorageMessage = {
  action: 'getItem' | 'setItem' | 'removeItem' | 'getAllKeys';
  key: string;
  value?: any;
  messageId: string;
};

const pendingRequests: Record<string, { resolve: Function; reject: Function }> = {};

let storageIframe: HTMLIFrameElement | null = null;
let isIframeReady = false;

let initializationPromise: Promise<void> | null = null;

/**
 * Every message handler must go through this: the sender has to be OUR storage
 * iframe (same window object) AND be served from the expected storage origin.
 */
const isMessageFromStorageFrame = (event: MessageEvent): boolean => {
  if (event.origin !== STORAGE_ORIGIN || !STORAGE_ORIGINS.includes(event.origin)) {
    return false;
  }

  return !!storageIframe?.contentWindow && event.source === storageIframe.contentWindow;
};

export const initCrossDomainStorage = (): Promise<void> => {
  if (CROSS_DOMAIN_STORAGE_DISABLED) {
    console.log('Cross-domain storage is disabled; skipping initialization');
    return Promise.resolve();
  }

  if (initializationPromise) {
    return initializationPromise;
  }

  if (storageIframe && isIframeReady) {
    console.log('Cross-domain storage already initialized and ready');
    return Promise.resolve();
  }

  console.log(`Initializing cross-domain storage with domain: ${STORAGE_DOMAIN}`);

  initializationPromise = new Promise<void>((resolve, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error('Cross-domain storage initialization timed out after 10 seconds'));
    }, 10000);

    const handleReadyMessage = (event: MessageEvent) => {
      if (!isMessageFromStorageFrame(event)) {
        return;
      }

      const { data } = event;
      if (data && data.ready === true) {
        clearTimeout(timeoutId);
        isIframeReady = true;
        console.log('Cross-domain storage iframe signaled ready');
        window.removeEventListener('message', handleReadyMessage);
        resolve();
      }
    };

    if (!storageIframe) {
      window.addEventListener('message', handleReadyMessage);

      window.addEventListener('message', handleStorageResponse, false);
      console.log('Message event listeners registered for cross-domain storage');

      storageIframe = document.createElement('iframe');
      storageIframe.id = 'cross-domain-storage-frame';
      storageIframe.src = `https://${STORAGE_DOMAIN}`;
      storageIframe.style.display = 'none';
      storageIframe.onerror = () => {
        clearTimeout(timeoutId);
        reject(new Error('Failed to load cross-domain storage iframe'));
      };
      document.body.appendChild(storageIframe);
      console.log('Cross-domain storage iframe created and appended to document');
    } else {
      window.addEventListener('message', handleReadyMessage);
    }
  }).catch((error) => {
    console.error('Cross-domain storage initialization failed:', error);
    initializationPromise = null;
    throw error;
  });

  return initializationPromise;
};

const handleStorageResponse = (event: MessageEvent) => {
  if (!isMessageFromStorageFrame(event)) {
    return;
  }

  if (!event.data || typeof event.data !== 'object') {
    console.error('Received invalid message from storage iframe:', event.data);
    return;
  }

  if (event.data.ready === true) {
    console.log('Received ready message from storage iframe');
    return;
  }

  const { messageId, value, error } = event.data;

  if (messageId && pendingRequests[messageId]) {
    try {
      if (error) {
        pendingRequests[messageId].reject(new Error(error));
      } else {
        pendingRequests[messageId].resolve(value);
      }
    } catch (e) {
      console.error('Error processing storage response:', e);
    } finally {
      delete pendingRequests[messageId];
    }
  } else if (messageId) {
    console.warn(`Received response for unknown request ID: ${messageId}`);
  }
};

const composeMessageId = () => {
  return `${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;
};

const sendMessage = (message: StorageMessage, retries = 2): Promise<any> => {
  return new Promise(async (resolve, reject) => {
    if (!storageIframe || !isIframeReady) {
      try {
        await initCrossDomainStorage();
      } catch (error) {
        reject(new Error(`Cross-domain storage initialization failed: ${error.message}`));
        return;
      }
    }

    const messageId = composeMessageId();

    console.log(`Sending ${message.action} request to storage iframe`, {
      key: message.key,
      requestId: messageId,
      hasValue: message.value !== undefined,
    });

    const timeoutId = setTimeout(() => {
      if (pendingRequests[messageId]) {
        delete pendingRequests[messageId];

        if (retries > 0) {
          console.warn(`Request ${messageId} timed out. Retrying... (${retries} retries left)`);
          sendMessage(message, retries - 1)
            .then(resolve)
            .catch(reject);
        } else {
          reject(new Error(`Request ${messageId} timed out after all retry attempts`));
        }
      }
    }, 5000);

    pendingRequests[messageId] = {
      resolve: (value) => {
        clearTimeout(timeoutId);
        resolve(value);
      },
      reject: (error) => {
        clearTimeout(timeoutId);
        reject(error);
      },
    };

    try {
      if (!storageIframe?.contentWindow) {
        throw new Error('Storage iframe or contentWindow is not available');
      }

      storageIframe.contentWindow.postMessage({ ...message, messageId }, STORAGE_ORIGIN);
    } catch (error) {
      clearTimeout(timeoutId);
      delete pendingRequests[messageId];

      if (retries > 0) {
        console.warn(
          `Failed to post message. Reinitializing and retrying... (${retries} retries left)`
        );
        if (storageIframe) {
          try {
            document.body.removeChild(storageIframe);
          } catch (e) {
            // Ignore if already removed
          }
          storageIframe = null;
          isIframeReady = false;
          initializationPromise = null;
        }

        setTimeout(() => {
          sendMessage(message, retries - 1)
            .then(resolve)
            .catch(reject);
        }, 500);
      } else {
        reject(error);
      }
    }
  });
};

export const getItemFromCrossDomainStorage = (key: string, defaultValue?: any): Promise<any> => {
  console.log(`Getting item from cross-domain storage: ${key}`);
  if (CROSS_DOMAIN_STORAGE_DISABLED || !isCrossDomainSyncableKey(key)) {
    return Promise.resolve(defaultValue);
  }
  return sendMessage({ action: 'getItem', key, messageId: composeMessageId() })
    .then((value) => {
      const result = value !== null ? value : defaultValue;
      console.log(`Retrieved item from cross-domain storage: ${key}`, {
        valueFound: value !== null,
        usingDefault: value === null && defaultValue !== undefined,
      });
      return result;
    })
    .catch((error) => {
      console.error(`Error getting item from cross-domain storage: ${key}`, error);
      console.log(`Returning default value for key: ${key}`);
      return defaultValue;
    });
};

export const setItemInCrossDomainStorage = (key: string, value: any): Promise<void> => {
  console.log(`Setting item in cross-domain storage: ${key}`, { valueType: typeof value });
  if (CROSS_DOMAIN_STORAGE_DISABLED || !isCrossDomainSyncableKey(key)) {
    return Promise.resolve();
  }
  return sendMessage({ action: 'setItem', key, value, messageId: composeMessageId() })
    .then(() => {
      console.log(`Successfully set item in cross-domain storage: ${key}`);
    })
    .catch((error) => {
      console.error(`Failed to set item in cross-domain storage: ${key}`, error);
      throw error;
    });
};

export const removeItemFromCrossDomainStorage = (key: string): Promise<void> => {
  console.log(`Removing item from cross-domain storage: ${key}`);
  if (CROSS_DOMAIN_STORAGE_DISABLED || !isCrossDomainSyncableKey(key)) {
    return Promise.resolve();
  }
  return sendMessage({ action: 'removeItem', key, messageId: composeMessageId() })
    .then(() => {
      console.log(`Successfully removed item from cross-domain storage: ${key}`);
    })
    .catch((error) => {
      console.error(`Failed to remove item from cross-domain storage: ${key}`, error);
      throw error;
    });
};

export const getAllKeysFromCrossDomainStorage = (): Promise<string[]> => {
  console.log('Getting all keys from cross-domain storage');
  if (CROSS_DOMAIN_STORAGE_DISABLED) {
    console.log('Cross-domain storage disabled; returning empty keys list');
    return Promise.resolve([]);
  }
  return sendMessage({ action: 'getAllKeys', key: '', messageId: composeMessageId() })
    .then((keys: string[]) => {
      console.log(`Retrieved ${keys?.length || 0} keys from cross-domain storage`);
      return (keys || []).filter(isCrossDomainSyncableKey);
    })
    .catch((error) => {
      console.error('Error getting all keys from cross-domain storage', error);
      return [];
    });
};

/**
 * Cleans up the cross-domain storage service by removing the iframe and event listeners.
 * Should be called when the application is unloaded or when the service is no longer needed.
 */
export const cleanupCrossDomainStorage = (): void => {
  // Remove event listener
  window.removeEventListener('message', handleStorageResponse);

  // Remove iframe if it exists
  if (storageIframe && document.body.contains(storageIframe)) {
    try {
      document.body.removeChild(storageIframe);
    } catch (e) {
      console.error('Error removing cross-domain storage iframe:', e);
    }
  }

  // Reset state
  storageIframe = null;
  isIframeReady = false;
  initializationPromise = null;

  // Clean up any pending requests
  for (const messageId in pendingRequests) {
    if (Object.prototype.hasOwnProperty.call(pendingRequests, messageId)) {
      pendingRequests[messageId].reject(new Error('Cross-domain storage service was cleaned up'));
      delete pendingRequests[messageId];
    }
  }

  console.log('Cross-domain storage service cleaned up');
};
